/**
 * Role Selector Dropdowns Component
 * 
 * Provides role switching functionality for users with multiple roles
 * and test account selection for superusers
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { ChevronDown } from 'lucide-react';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Select } from '../../design-system';
import ChurchSwitcher from './ChurchSwitcher';
import AsyncSelect from './AsyncSelect';

const DropdownContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
`;

const DropdownWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const DropdownLabel = styled.span`
  font-size: var(--font-size-xs);
  color: var(--text-tertiary);
  margin-bottom: 4px;
  text-transform: uppercase;
  font-weight: var(--font-weight-semibold);
`;

const StyledAsyncSelect = styled(AsyncSelect)`
  min-width: 180px;
  
  .select-control {
    background: var(--background-secondary);
    border: 1px solid var(--border-light);
    font-size: var(--font-size-sm);
    
    &:hover {
      border-color: var(--primary-400);
    }
  }
`;

const RoleBadge = styled.span<{ $roleType: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  background: ${props => {
    switch (props.$roleType) {
      case 'SUPERADMIN': return 'var(--error-100)';
      case 'PASTOR': return 'var(--primary-100)';
      case 'ADMIN': return 'var(--warning-100)';
      case 'MINISTER': return 'var(--success-100)';
      case 'LEADER': return 'var(--info-100)';
      case 'BISHOP': return 'var(--purple-100)';
      case 'JURISDICTION_ADMIN': return 'var(--purple-100)';
      case 'SUPERINTENDENT': return 'var(--indigo-100)';
      default: return 'var(--background-tertiary)';
    }
  }};
  color: ${props => {
    switch (props.$roleType) {
      case 'SUPERADMIN': return 'var(--error-700)';
      case 'PASTOR': return 'var(--primary-700)';
      case 'ADMIN': return 'var(--warning-700)';
      case 'MINISTER': return 'var(--success-700)';
      case 'LEADER': return 'var(--info-700)';
      case 'BISHOP': return 'var(--purple-700)';
      case 'JURISDICTION_ADMIN': return 'var(--purple-700)';
      case 'SUPERINTENDENT': return 'var(--indigo-700)';
      default: return 'var(--text-primary)';
    }
  }};
`;

interface UserRole {
  id: string;
  role_type: string;
  role_display: string;
  church_name?: string;
  ministry_names?: string[];
  group_names?: string[];
  jurisdiction?: string;
  district?: string;
}

interface DenominationConfig {
  plugin_name: string;
  has_hierarchy: boolean;
  requires_hierarchy: boolean;
  hierarchy_levels?: string[];
  available_roles?: string[];
  ui_elements?: any;
  terminology?: any;
}

interface RoleSelectorDropdownsProps {
  showChurchSelector?: boolean;
}

const RoleSelectorDropdowns: React.FC<RoleSelectorDropdownsProps> = ({ 
  showChurchSelector = false 
}) => {
  const { user, refreshUser } = useAuth();
  const [availableRoles, setAvailableRoles] = useState<UserRole[]>([]);
  const [activeRole, setActiveRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentContext, setCurrentContext] = useState<'church' | 'jurisdiction'>('church');
  const [denominationConfig, setDenominationConfig] = useState<DenominationConfig | null>(null);
  const [hasLoadedInitialRole, setHasLoadedInitialRole] = useState(false);

  // Only load saved context on mount, not data (lazy loading)
  useEffect(() => {
    // Get saved context
    const savedContext = localStorage.getItem('selectedContext') as 'church' | 'jurisdiction' | null;
    if (savedContext) {
      setCurrentContext(savedContext);
    }
    
    // Only load the active role to display current selection
    // Other data will be loaded lazily when dropdowns are opened
    loadActiveRoleOnly();
  }, [user]);

  // Load only the active role (for display) without loading all options
  const loadActiveRoleOnly = async () => {
    if (!user || hasLoadedInitialRole) return;

    try {
      // Use unifiedApi which properly handles authentication
      const { unifiedApi } = await import('../../services/UnifiedApiService');
      const response = await unifiedApi.get(`/auth/roles/available/?context=${currentContext}`);
      if (response.active_role) {
        setActiveRole(response.active_role);
        // Only add the active role to available roles for initial display
        setAvailableRoles([response.active_role]);
      }
      setHasLoadedInitialRole(true);
    } catch (error) {
      // Silently fail for authentication errors - user is likely not logged in yet
      if (!error.message?.includes('No authentication token available')) {
        console.error('Failed to load active role:', error);
      }
    }
  };

  const fetchDenominationConfig = async (context: 'church' | 'jurisdiction') => {
    try {
      const response = await api.get('/churches/denomination-configs/for_current_context/', {
        params: { context }
      });
      setDenominationConfig(response.data);
      return response.data;
    } catch (err: any) {
      // Silently handle authentication errors (normal when not logged in)
      if (!err?.message?.includes('No authentication token')) {
        console.error('Failed to fetch denomination config:', err);
      }
      // Set default config
      const defaultConfig = {
        plugin_name: 'non_denominational',
        has_hierarchy: false,
        requires_hierarchy: false
      };
      setDenominationConfig(defaultConfig);
      return defaultConfig;
    }
  };

  // Lazy load roles - returns options for AsyncSelect
  const loadRolesLazy = useCallback(async () => {
    try {
      // Ensure denomination config is loaded first
      if (!denominationConfig) {
        await fetchDenominationConfig(currentContext);
      }
      
      const response = await api.get(`/auth/roles/available/?context=${currentContext}`);
      
      // Combine roles based on context
      let allRoles = [];
      if (currentContext === 'jurisdiction') {
        // In jurisdiction context, only show denomination roles
        allRoles = response.data.grouped_roles.denomination_roles || [];
        
        // Filter based on denomination config available roles
        if (denominationConfig?.available_roles) {
          allRoles = allRoles.filter((role: UserRole) => 
            denominationConfig.available_roles!.includes(role.role_type)
          );
        }
      } else {
        // In church context, show church, ministry, and group roles
        allRoles = (response.data.grouped_roles.church_roles || [])
          .concat(response.data.grouped_roles.ministry_roles || [])
          .concat(response.data.grouped_roles.group_roles || []);
          
        // For non-hierarchical denominations, exclude hierarchy roles
        if (!denominationConfig?.has_hierarchy) {
          allRoles = allRoles.filter((role: UserRole) => 
            !['BISHOP', 'SUPERINTENDENT', 'JURISDICTION_ADMIN'].includes(role.role_type)
          );
        }
      }
      
      // Include the active role if it's not already in the list
      if (response.data.active_role) {
        const activeRoleId = response.data.active_role.id;
        const isActiveRoleInList = allRoles.some((role: UserRole) => role.id === activeRoleId);
        
        if (!isActiveRoleInList) {
          allRoles.unshift(response.data.active_role); // Add active role at the beginning
        }
        setActiveRole(response.data.active_role);
      }
      
      setAvailableRoles(allRoles);
      
      // Return options formatted for Select
      return allRoles.map(formatRoleOption);
    } catch (error: any) {
      // Silently handle authentication errors (normal when not logged in)
      if (!error?.message?.includes('No authentication token')) {
        console.error('Failed to load roles:', error);
      }
      // Return empty array for auth errors, throw other errors
      if (error?.message?.includes('No authentication token')) {
        return [];
      }
      throw error;
    }
  }, [currentContext, denominationConfig]);

  const handleRoleSwitch = async (roleId: string) => {
    try {
      await api.post('/auth/roles/switch/', { role_id: roleId });
      await loadRolesLazy();
      // Refresh the page or update context to reflect new role
      await refreshUser();
      window.location.reload(); // Simple reload to update all components
    } catch (error) {
      console.error('Failed to switch role:', error);
    }
  };

  const formatRoleOption = (role: UserRole) => {
    let label = role.role_display;
    
    // Add context
    if (role.ministry_names && role.ministry_names.length > 0) {
      label += ` - ${role.ministry_names.join(', ')}`;
    } else if (role.group_names && role.group_names.length > 0) {
      label += ` - ${role.group_names.join(', ')}`;
    } else if (role.jurisdiction) {
      label += ` - ${role.jurisdiction}`;
    } else if (role.district) {
      label += ` - ${role.district}`;
    }
    
    return {
      value: role.id,
      label: label,
      roleType: role.role_type
    };
  };

  // Check if user has denominational role (can switch between churches)
  const hasDenominationalRole = activeRole?.role_type &&
    ['BISHOP', 'JURISDICTION_ADMIN', 'SUPERINTENDENT', 'SUPERADMIN'].includes(activeRole.role_type);

  return (
    <DropdownContainer>
      {/* Church Selector - Only for superusers and denominational roles */}
      {(user?.is_superuser || hasDenominationalRole) && showChurchSelector && (
        <DropdownWrapper>
          <ChurchSwitcher />
        </DropdownWrapper>
      )}

      {/* Role Selector - Always show for all authenticated users */}
      {(activeRole || user) && (
        <DropdownWrapper>
          <StyledAsyncSelect
            value={activeRole?.id || ''}
            onChange={(value) => handleRoleSwitch(value as string)}
            loadOptions={loadRolesLazy}
            initialOptions={activeRole ? [formatRoleOption(activeRole)] : []}
            placeholder="Select role..."
            cacheOptions={true}
            renderOption={(option: any) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <RoleBadge $roleType={option.roleType}>
                  {option.roleType}
                </RoleBadge>
                <span>{option.label}</span>
              </div>
            )}
          />
        </DropdownWrapper>
      )}
    </DropdownContainer>
  );
};

export default RoleSelectorDropdowns;